<template>
  <main class="rh-profile">
    <rh-cabecalho
      pagina="pessoas"
      subtitulo="PESSOAS E PERFIS"
      titulo=""
      :unidade-id="unidadeId"
      :empresas="empresas"
      mostrar-pessoas
      :mostrar-configuracao="podeAdministrar"
      @empresa-selecionada="selecionarEmpresa"
    />
    <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
    <b-loading :active="loading" :is-full-page="false"></b-loading>

    <section v-if="!loading && unidadeId && perfilDisponivel" class="rh-card">
      <div class="rh-card-head">
        <div>          
          <h1>{{ edicao ? 'Editar perfil' : 'Novo perfil' }}</h1>
        </div>
        <b-button type="is-light" icon-left="arrow-left" @click="voltar">Voltar aos perfis</b-button>
      </div>

      <ValidationObserver v-slot="{ handleSubmit }" slim>
        <form @submit.prevent="handleSubmit(salvar)">
          <div class="columns">
            <div class="column is-two-thirds">
              <ValidationProvider v-slot="{ errors, valid }" name="Nome" rules="required|max:50">
                <b-field label="Nome" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                  <b-input v-model.trim="model.nome" maxlength="50" :has-counter="false"></b-input>
                </b-field>
              </ValidationProvider>
            </div>
            <div class="column">
              <b-field label="Código"><b-input :value="model.id || 'Novo'" disabled></b-input></b-field>
            </div>
          </div>

          <div class="rh-options">                
            <b-field v-if="model.bloquearAcessoAtrasos" label="Quantidade de atrasos">
              <b-input v-model.number="model.quantidadeAtrasos" type="number" min="0"></b-input>
            </b-field>
          </div>

          <cadastro-items
            v-if="model.administrador"
            label="Unidades de acesso"
            :items="model.unidadesDeAcesso"
            tabela="unidade"
            @update:items="model.unidadesDeAcesso = $event"
          />

          <div class="rh-section-head">
            <h2>Regras de acesso</h2>
            <div class="rh-actions">
              <b-button type="is-light" icon-left="content-copy" native-type="button" @click="abrirCopia">Copiar de outro perfil</b-button>
              <b-switch :value="todasMarcadas" @input="marcarTodas">Marcar todas</b-switch>
            </div>
          </div>
          <div v-if="!grupos.length" class="rh-empty">Nenhuma regra disponível.</div>
          <b-collapse v-for="grupo in grupos" :key="grupo.nome" class="rh-rule-group" :open="true" animation="slide">
            <template #trigger="props">
              <div class="rh-rule-heading" role="button">
                <strong>{{ grupo.nome === 'MetaVirtue' ? grupo.nome + ' - Selecionar apenas um' : grupo.nome }}</strong>
                <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'"></b-icon>
              </div>
            </template>
            <div class="rh-rule-grid">
              <label v-for="regra in grupo.regras" :key="regra.id" class="rh-rule">
                <input type="checkbox" :checked="regraSelecionada(regra)" @change="alternarRegra(regra)">
                <span>{{ regra.descricao || regra.nome }}</span>
              </label>
            </div>
          </b-collapse>

          <historico-alteracoes v-if="model.id" tabela="NivelDeAcesso" :chave="model.id" />

          <div class="rh-form-actions">
            <b-button native-type="submit" type="is-success" icon-left="check-circle" :loading="salvando">Salvar</b-button>
            <b-button native-type="button" type="is-light" @click="voltar">Cancelar</b-button>
          </div>
        </form>
      </ValidationObserver>
    </section>

    <b-modal :active.sync="modalCopia" has-modal-card trap-focus>
      <div class="modal-card">
        <header class="modal-card-head"><p class="modal-card-title">Copiar regras de outro perfil</p></header>
        <section class="modal-card-body">
          <b-field label="Perfil">
            <b-select v-model="perfilCopiaId" expanded placeholder="Selecione um perfil">
              <option v-for="perfil in perfisCopia" :key="perfil.id" :value="perfil.id">{{ perfil.nome }}</option>
            </b-select>
          </b-field>
          <p v-if="!perfisCopia.length" class="rh-muted">Nenhum outro perfil disponível nesta empresa.</p>
        </section>
        <footer class="modal-card-foot">
          <b-button type="is-primary" :disabled="!perfilCopiaId" @click="copiarRegras">Copiar</b-button>
          <b-button @click="modalCopia = false">Cancelar</b-button>
        </footer>
      </div>
    </b-modal>
  </main>
</template>

<script>
import http from '@/http'
import RhCabecalho from '@/components/rh_cabecalho.vue'
import CadastroItems from '@/components/manutencao/cadastroItems.vue'
import HistoricoAlteracoes from '@/components/historicoAlteracoes.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'RhPerfil',
  components: { RhCabecalho, CadastroItems, HistoricoAlteracoes },
  data() {
    return {
      empresas: [],
      unidadeId: null,
      model: {
        id: null,
        nome: '',
        administrador: false,
        bloquearAcessoAtrasos: false,
        quantidadeAtrasos: 0,
        unidadesDeAcesso: [],
        regras: [],
        regrasExistentes: [],
      },
      loading: false,
      salvando: false,
      erro: '',
      modalCopia: false,
      perfisCopia: [],
      perfilCopiaId: null,
      perfilDisponivel: true,
    }
  },
  computed: {
    ...mapGetters(['isInRole']),
    edicao() { return !!this.$route.params.id },
    podeAdministrar() { return this.isInRole('rh-diretoria') },
    grupos() {
      const grupos = new Map()
      const regrasExistentes = this.model.regrasExistentes || []
      regrasExistentes.forEach((regra) => {
        const nome = regra.grupo || 'Outras regras'
        if (!grupos.has(nome)) grupos.set(nome, [])
        grupos.get(nome).push(regra)
      })
      return Array.from(grupos, ([nome, regras]) => ({ nome, regras }))
    },
    todasMarcadas() {
      const regras = this.model.regrasExistentes || []
      return regras.length > 0 && regras.every(this.regraSelecionada)
    },
  },
  created() { this.iniciar() },
  methods: {
    falha(e) { this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível concluir a operação. Tente novamente.' },
    voltar() { this.$router.push({ name: 'rh-pessoas', query: { unidadeId: this.unidadeId, aba: 'perfis' } }) },
    selecionarEmpresa(id) {
      if (this.edicao) { this.$router.push({ name: 'rh-pessoas', query: { unidadeId: id, aba: 'perfis' } }); return }
      this.unidadeId = id
      this.$router.replace({ name: 'rh-perfil-novo', query: { unidadeId: id } })
    },
    async iniciar() {
      this.loading = true
      try {
        this.empresas = (await http.get('/api/rh/estrutura/empresas')).data || []
        const rota = Number(this.$route.query.unidadeId)
        const atual = Number(this.$store.state.unidadeId)
        this.unidadeId = this.empresas.some((x) => x.id === rota) ? rota
          : this.empresas.some((x) => x.id === atual) ? atual
            : (this.empresas[0] && this.empresas[0].id) || null
        if (!this.unidadeId) { this.erro = 'Nenhuma empresa disponível para o seu perfil.'; return }
        if (this.edicao) {
          const { data: perfis } = await http.get('/api/search/ListarNiveisAcesso', { params: { unidadeId: this.unidadeId } })
          if (!(perfis || []).some((perfil) => perfil.id === Number(this.$route.params.id))) {
            this.perfilDisponivel = false
            this.erro = 'Perfil não encontrado nesta empresa.'
            return
          }
        }
        const { data } = await http.get('/api/manutencao/niveldeacesso', {
          params: this.edicao ? { id: Number(this.$route.params.id) } : undefined,
        })
        this.model = {
          ...this.model,
          ...data,
          regras: data.regras || [],
          regrasExistentes: data.regrasExistentes || [],
          unidadesDeAcesso: data.unidadesDeAcesso || [],
        }
      } catch (e) { this.perfilDisponivel = false; this.falha(e) } finally { this.loading = false }
    },
    regraSelecionada(regra) { return (this.model.regras || []).some((item) => item.id === regra.id) },
    alternarRegra(regra) {
      const regras = this.model.regras || []
      this.model.regras = this.regraSelecionada(regra)
        ? regras.filter((item) => item.id !== regra.id)
        : [...regras, { id: regra.id, nome: regra.nome }]
    },
    marcarTodas(marcar) {
      this.model.regras = marcar
        ? this.model.regrasExistentes.map((regra) => ({ id: regra.id, nome: regra.nome }))
        : []
    },
    async abrirCopia() {
      try {
        const { data } = await http.get('/api/search/ListarNiveisAcesso', { params: { unidadeId: this.unidadeId } })
        this.perfisCopia = (data || []).filter((perfil) => perfil.id !== this.model.id)
        this.perfilCopiaId = null
        this.modalCopia = true
      } catch (e) { this.falha(e) }
    },
    copiarRegras() {
      const perfil = this.perfisCopia.find((item) => item.id === this.perfilCopiaId)
      if (!perfil) return
      this.model.regras = (perfil.regras || []).map((regra) => ({ id: regra.id, nome: regra.nome }))
      this.modalCopia = false
    },
    async salvar() {
      if (!this.unidadeId) return
      this.salvando = true
      this.erro = ''
      try {
        const model = {
          ...this.model,
          unidadeDeCadastro: { id: this.unidadeId },
          unidadesDeAcesso: this.model.administrador ? this.model.unidadesDeAcesso : [],
          quantidadeAtrasos: this.model.bloquearAcessoAtrasos ? this.model.quantidadeAtrasos : 0,
        }
        await http.post('/api/manutencao/nivel', model)
        this.$buefy.toast.open({ message: 'Perfil salvo.', type: 'is-success' })
        this.voltar()
      } catch (e) { this.falha(e) } finally { this.salvando = false }
    },
  },
}
</script>

<style scoped>
.rh-profile { --navy: #0b1a3e; --teal: #0e8fa8; background: #f4f3f0; min-height: 100vh; padding: 0 28px 48px; border-radius: 18px; color: #1c1d24; font-family: Montserrat, Arial, sans-serif; }
.rh-card { background: #fff; border: 1px solid #e7e9ed; border-radius: 17px; padding: 28px; margin: 16px 0; box-shadow: 0 8px 20px rgba(11, 26, 62, .04); }
.rh-card-head, .rh-section-head, .rh-actions, .rh-form-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.rh-card h1 { color: var(--navy); font-size: 24px; font-weight: 800; }
.rh-card h2 { color: var(--navy); font-size: 18px; font-weight: 700; }
.rh-kicker { color: var(--teal); font-size: 11px; font-weight: 800; letter-spacing: .15em; }
.rh-options { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; margin: 24px 0; }
.rh-options .field { margin-bottom: 0; width: 180px; }
.rh-section-head { margin: 30px 0 14px; }
.rh-rule-group { border: 1px solid #e7e9ed; border-radius: 12px; margin: 10px 0; overflow: hidden; }
.rh-rule-heading { display: flex; justify-content: space-between; align-items: center; padding: 13px 17px; background: #f7f8fa; cursor: pointer; }
.rh-rule-grid { display: grid; grid-template-columns: 1fr; gap: 12px; padding: 18px; }
.rh-rule { display: flex; gap: 10px; align-items: flex-start; cursor: pointer; }
.rh-rule input { margin-top: 5px; }
.rh-form-actions { justify-content: flex-end; margin-top: 28px; }
.rh-empty, .rh-muted { color: #667085; }
@media (max-width: 640px) { .rh-profile { padding: 0 14px 30px; } .rh-card { padding: 18px; } }
</style>
